import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { demos, isLegacyDemo } from './registry';
import { Sparkles, ArrowRight } from 'lucide-react';

const DemoSelector: React.FC = () => {
  const navigate = useNavigate();

  const handleSelectDemo = (demoId: string) => {
    localStorage.setItem('selected-demo', demoId);
    // Kiehl's uses the legacy shared views with brand parameter
    if (demoId === 'kiehls') {
      navigate(`/?brand=kiehls`);
    } else {
      // All other demos have direct routes: /nespresso, /adidas, /nike, etc.
      navigate(`/${demoId}`);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 py-12">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Pentatonic Logo */}
          <div className="flex items-center justify-center mb-8">
            <img
              src="/assets/images/logos/pentatonic.png"
              alt="Pentatonic"
              className="h-8"
            />
          </div>

          <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-4 tracking-tight">
            Physical Intelligence Node
          </h1>
          <p className="text-xl text-gray-500 font-light">
            Select a brand experience to begin
          </p>
        </motion.div>

        {/* Demo Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          {demos.map((demo, index) => {
            const isIsolated = !isLegacyDemo(demo.id);

            return (
              <motion.button
                key={demo.id}
                onClick={() => handleSelectDemo(demo.id)}
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden bg-gray-50 border border-gray-200 hover:border-gray-400 hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ scale: 1.02, y: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Background gradient based on brand color */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `radial-gradient(circle at 50% 100%, ${demo.color}20, transparent 70%)`
                  }}
                />

                {/* Content */}
                <div className="relative h-full flex flex-col items-center justify-between p-6">
                  {/* Badge for isolated demos */}
                  {isIsolated && (
                    <div className="absolute top-4 right-4 flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs px-2 py-1 rounded-full">
                      <Sparkles className="w-3 h-3" />
                      <span>New</span>
                    </div>
                  )}

                  {/* Logo area */}
                  <div className="flex-1 flex items-center justify-center w-full">
                    <div
                      className="w-32 h-32 rounded-2xl flex items-center justify-center p-4 transition-transform duration-300 group-hover:scale-110 bg-white border border-gray-100"
                    >
                      <img
                        src={demo.logo}
                        alt={`${demo.name} logo`}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          // Show fallback letter if logo fails
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = `<span class="text-4xl font-bold text-gray-800">${demo.name.charAt(0)}</span>`;
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Text */}
                  <div className="text-center">
                    <h3 className="text-xl font-medium text-gray-900 mb-2">{demo.name}</h3>
                    <p className="text-sm text-gray-500 mb-4">{demo.description}</p>

                    {/* Features */}
                    <div className="flex flex-wrap justify-center gap-2 mb-4">
                      {demo.features?.aiVision && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          AI Vision
                        </span>
                      )}
                      {demo.features?.gamification && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          Gamification
                        </span>
                      )}
                      {demo.features?.loyalty && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          Loyalty
                        </span>
                      )}
                      {demo.features?.cashRewards && (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                          Cash
                        </span>
                      )}
                    </div>

                    {/* Launch button */}
                    <div
                      className="flex items-center justify-center gap-2 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity text-gray-900"
                    >
                      <span>Launch Demo</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      </div>

      {/* Footer */}
      <motion.footer
        className="relative z-10 text-center py-8 text-gray-400 text-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <p>Physical Intelligence Node v2.0 — Pentatonic Ltd.</p>
      </motion.footer>
    </div>
  );
};

export default DemoSelector;
